<script lang="ts">
  import { getGtmContainerId } from "$lib/environment"
  import type { WebViewPlatform } from "$lib/util"
  import { onMount } from "svelte"
  import { setAmplitudeOptOut } from "@tastyworks/shared-amplitude-analytics/amplitude"
  import {
    OneTrustCategory,
    flushGtmBuffer,
    gtag,
    loadGoogleTagManager,
    setGrantedCategories,
  } from "."

  function getConsentState(
    category: OneTrustCategory,
    groups: OneTrustCategory[]
  ) {
    return groups.includes(category) ? "granted" : "denied"
  }
  interface Props {
    isWebView?: boolean
    platform?: WebViewPlatform
    isIosAttAuthorized?: boolean
  }

  let {
    isWebView = false,
    platform = null,
    isIosAttAuthorized = false,
  }: Props = $props()

  const WEBVIEW_IMPLIED_CONSENT_CATEGORIES: OneTrustCategory[] = [
    OneTrustCategory.strictlyNecessary,
    OneTrustCategory.performance,
    OneTrustCategory.functional,
  ]

  function handleConsentChange(event: CustomEvent<OneTrustCategory[]>) {
    setGrantedCategories(event.detail)
    setAmplitudeOptOut(!event.detail.includes(OneTrustCategory.performance))
    gtag([
      "consent",
      "default",
      {
        ad_storage: getConsentState(OneTrustCategory.targeting, event.detail),
        analytics_storage: getConsentState(
          OneTrustCategory.performance,
          event.detail
        ),
        functionality_storage: getConsentState(
          OneTrustCategory.functional,
          event.detail
        ),
        personalization_storage: getConsentState(
          OneTrustCategory.strictlyNecessary,
          event.detail
        ),
        security_storage: getConsentState(
          OneTrustCategory.strictlyNecessary,
          event.detail
        ),
        wait_for_update: 500,
      },
    ])
    const gtmContainerId = getGtmContainerId()
    if (gtmContainerId) {
      loadGoogleTagManager(gtmContainerId)
    }
  }

  // With no banner on iOS, ATT is where the targeting answer comes from:
  //   declined   — a banner must not contradict that answer, so nothing is granted.
  //   authorized — imply the category rather than interrupt the flow to ask again.
  // Running the banner without a native ATT prompt is what got 3.29.4 rejected.
  function impliedCategories() {
    return isIosAttAuthorized
      ? [...WEBVIEW_IMPLIED_CONSENT_CATEGORIES, OneTrustCategory.targeting]
      : WEBVIEW_IMPLIED_CONSENT_CATEGORIES
  }

  function initializeOneTrust() {
    // Apple requires an in-app web view to respect the ATT answer, not ask its own.
    if (isWebView && platform !== "android") {
      window.dispatchEvent(
        new CustomEvent("OneTrustGroupsUpdated", {
          detail: impliedCategories(),
        })
      )
    } else {
      createScript(
        "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js",
        "b01342ba-67b5-4080-933e-d17d3e85fdee"
      )
    }
  }

  window.addEventListener("snowPlowLoaded", flushGtmBuffer)
  window.addEventListener("OneTrustGroupsUpdated", handleConsentChange)

  initializeOneTrust()

  onMount(() => {
    return () => {
      window.removeEventListener("snowPlowLoaded", flushGtmBuffer)
      window.removeEventListener("OneTrustGroupsUpdated", handleConsentChange)
    }
  })

  function createScript(src: string, domainScript?: string) {
    const scriptTag = document.createElement("script")
    scriptTag.src = src
    if (domainScript) {
      scriptTag.dataset.domainScript = domainScript
    }
    const earliestScriptTagMounted = document.getElementsByTagName("script")[0]
    scriptTag.type = "text/javascript"
    earliestScriptTagMounted.parentNode?.insertBefore(
      scriptTag,
      earliestScriptTagMounted
    )
    return scriptTag
  }
</script>
